/**
 * ContactLensesSection Component
 *
 * Displays contact lens data in a tabular format showing OD (right eye) and OS (left eye)
 * measurements including sphere, cylinder, axis, add, base curve, diameter, and visual acuity.
 * Provides an editable interface for updating contact lens prescriptions.
 *
 * Follows Apple's design guidelines with consistent glass morphism UI and accessibility features.
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { CheckCircle, Edit3, Loader2, Save, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Textarea } from './ui/textarea';

interface ContactLensData {
  // OD (Right Eye) fields
  odSph?: string;
  odCyl?: string;
  odAxis?: string;
  odAdd?: string;
  odDVA?: string;
  odNVA?: string;
  odBC?: string;
  odDia?: string;
  odND?: string;

  // OS (Left Eye) fields
  osSph?: string;
  osCyl?: string;
  osAxis?: string;
  osAdd?: string;
  osDVA?: string;
  osNVA?: string;
  osBC?: string;
  osDia?: string;
  osND?: string;

  // Both Eyes fields
  ouDVA?: string;
  ouNVA?: string;

  // Additional fields for contact lenses
  usage?: string;
  notes?: string;

  // Lens specific fields (mainly for finalsoft)
  odLensManufacturer?: string;
  osLensManufacturer?: string;
  odLensName?: string;
  osLensName?: string;
  odLensColor?: string;
  osLensColor?: string;
  odLensEnhOpq?: string; // Enhancement/Opacity
  osLensEnhOpq?: string; // Enhancement/Opacity

  [key: string]: any; // Allow for other properties in the JSON
}

// Interface for the API response structure
interface ContactLensApiResponse {
  testKey: string;
  data: ContactLensData[];
  segmentIds?: string[];
}

interface ContactLensesSectionProps {
  jsonContent: ContactLensData | ContactLensApiResponse[] | null;
  isLoading?: boolean;
  error?: string | null;
  index?: number;
  onUpdate: (updatedData: ContactLensApiResponse[]) => Promise<void>;
  onAccept?: () => void;
}

const ContactLensesSection = ({
  jsonContent,
  isLoading = false,
  error = null,
  index = 0,
  onUpdate,
  onAccept,
}: ContactLensesSectionProps) => {
  const [currentData, setCurrentData] = useState<Record<
    string,
    ContactLensData
  > | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, ContactLensData>>(
    {}
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [acceptedSections, setAcceptedSections] = useState<Set<string>>(
    new Set()
  );

  // Helper function to process API response into ContactLensData
  const processApiResponse = useCallback(
    (
      content: ContactLensApiResponse[] | ContactLensData | null
    ): Record<string, ContactLensData> | null => {
      if (!content) return null;

      // If it's already processed data (single object), convert to record format
      if (!Array.isArray(content)) {
        return { contactlens: content };
      }

      // Process API response array with new structure
      const processedData: Record<string, ContactLensData> = {};

      content.forEach((item) => {
        if (item.data && item.data.length > 0) {
          const data = item.data[0]; // Get first item from data array

          // Extract the contact lens type from testKey (e.g., "contactlenses.habitualsoft" -> "habitualsoft")
          const contactLensType = item.testKey.replace('contactlenses.', '');

          processedData[contactLensType] = data;
        }
      });

      return processedData;
    },
    []
  );

  // Helper function to convert contact lens data back to API response format
  const convertToApiResponse = useCallback(
    (
      contactLensData: Record<string, ContactLensData>
    ): ContactLensApiResponse[] => {
      const apiResponse: ContactLensApiResponse[] = [];

      Object.entries(contactLensData).forEach(([contactLensType, data]) => {
        // Convert contact lens type back to testKey format (e.g., "habitualsoft" -> "contactlenses.habitualsoft")
        const testKey = `contactlenses.${contactLensType}`;

        apiResponse.push({
          testKey,
          data: [data], // Wrap in array as expected by API
          segmentIds: [], // Add empty segmentIds array
        });
      });

      return apiResponse;
    },
    []
  );

  // Update local state when jsonContent changes
  useEffect(() => {
    const processedData = processApiResponse(jsonContent);
    setCurrentData(processedData);
  }, [jsonContent, processApiResponse]);

  // Handle updates to individual contact lens sections
  const handleContactLensUpdate = useCallback(
    async (contactLensType: string, updatedFields: Record<string, string>) => {
      if (!currentData) return;

      const updatedData: Record<string, ContactLensData> = {
        ...currentData,
        [contactLensType]: {
          ...currentData[contactLensType],
          ...updatedFields,
        },
      };

      // Update local state optimistically
      setCurrentData(updatedData);

      try {
        // Convert the updated data to API response format and send it
        const apiResponseData = convertToApiResponse(updatedData);
        await onUpdate(apiResponseData);
      } catch (error) {
        // Revert local state on error
        const processedData = processApiResponse(jsonContent);
        setCurrentData(processedData);
        throw error;
      }
    },
    [
      currentData,
      jsonContent,
      onUpdate,
      processApiResponse,
      convertToApiResponse,
    ]
  );

  // Handle accepting all contact lens sections
  const handleAcceptAll = useCallback(() => {
    if (!currentData) return;

    // Mark all sections as accepted
    const allSections = new Set(Object.keys(currentData));
    setAcceptedSections(allSections);

    // Call global accept
    if (onAccept) {
      onAccept();
    }

    console.log('✅ Accepted all contact lens sections');
  }, [currentData, onAccept]);

  // Handle edit button click for all sections
  const handleEditAllClick = useCallback(() => {
    if (!currentData) return;

    // Combine all contact lens data into a single edit object
    const allEditValues: Record<string, ContactLensData> = {};

    // Include ALL contact lens sections for editing
    Object.entries(currentData).forEach(([key, data]) => {
      allEditValues[key] = data;
    });

    console.log(
      '🔍 Opening edit dialog with all contact lens values:',
      allEditValues
    );
    setEditValues(allEditValues);
    setIsEditDialogOpen(true);
  }, [currentData]);

  // Handle saving all edited values
  const handleSaveEdit = useCallback(async () => {
    if (!currentData) return;

    setIsUpdating(true);
    try {
      // Update all contact lens data with edited values
      const updatedData: Record<string, ContactLensData> = {
        ...currentData,
        ...editValues,
      };

      // Update local state optimistically
      setCurrentData(updatedData);

      // Convert the updated data to API response format and send it
      const apiResponseData = convertToApiResponse(updatedData);

      // Call onUpdate with the properly formatted data
      await onUpdate(apiResponseData);

      // Close the dialog
      setIsEditDialogOpen(false);
      setEditValues({});

      console.log('✅ Updated all contact lens sections:', {
        editValues,
        apiFormat: apiResponseData,
      });
    } catch (error) {
      console.error('Failed to update contact lens sections:', error);
      // Revert local state on error
      const processedData = processApiResponse(jsonContent);
      setCurrentData(processedData);
    } finally {
      setIsUpdating(false);
    }
  }, [
    editValues,
    currentData,
    onUpdate,
    jsonContent,
    processApiResponse,
    convertToApiResponse,
  ]);

  // Handle canceling edit
  const handleCancelEdit = useCallback(() => {
    setIsEditDialogOpen(false);
    setEditValues({});
  }, []);

  // Handle input changes in edit form for specific contact lens type and field
  const handleInputChange = useCallback(
    (contactLensType: string, field: string, value: string) => {
      setEditValues((prev) => ({
        ...prev,
        [contactLensType]: {
          ...prev[contactLensType],
          [field]: value,
        },
      }));
    },
    []
  );

  // Check if a contact lens section has meaningful data
  const hasContactLensData = (data: ContactLensData): boolean => {
    if (!data) return false;

    // Check for core contact lens fields
    const coreFields = [
      'odSph',
      'odCyl',
      'odAxis',
      'odAdd',
      'odDVA',
      'odNVA',
      'odBC',
      'odDia',
      'osSph',
      'osCyl',
      'osAxis',
      'osAdd',
      'osDVA',
      'osNVA',
      'osBC',
      'osDia',
      'ouDVA',
      'ouNVA',
    ];

    return coreFields.some(
      (field) =>
        data[field] &&
        typeof data[field] === 'string' &&
        data[field].trim() !== '' &&
        data[field] !== 'NaN'
    );
  };

  // Get contact lens sections that have data
  const contactLensTypeDisplayNames: Record<string, string> = {
    trial: 'Trial',
    habitualsoft: 'Habitual',
    finalsoft: 'Final',
  };

  const contactLensSections = currentData
    ? Object.entries(currentData)
        .filter(([key, data]) => hasContactLensData(data))
        .map(([key, data]) => ({
          key,
          title:
            (contactLensTypeDisplayNames[key] ||
              key.charAt(0).toUpperCase() + key.slice(1)) + ' Contact Lens',
          data,
        }))
    : [];

  // Check if any contact lens sections have data
  const hasAnyContactLensData = contactLensSections.length > 0;

  // Define table headers
  const headers = [
    '',
    'Sph',
    'Cyl',
    'Axis',
    'Add',
    'BC',
    'Dia',
    'DVA',
    'NVA',
    'N/D',
  ];

  // Helper function to get display value with fallback
  const getDisplayValue = (value: string | undefined) => {
    if (!value || value === 'NaN' || value.trim() === '') return '—';
    return value;
  };

  // Render contact lens table for a given data set
  const renderContactLensTable = (data: ContactLensData) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[620px]">
        <thead>
          <tr>
            {headers.map((header, idx) => (
              <th
                key={idx}
                className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 text-center whitespace-nowrap">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* OD (Right Eye) Row */}
          <tr>
            <td className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 text-center">
              <strong>OD</strong>
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odSph)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odCyl)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odAxis)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odAdd)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odBC)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odDia)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odDVA)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odNVA)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odND)}
            </td>
          </tr>

          {/* OS (Left Eye) Row */}
          <tr>
            <td className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 text-center">
              <strong>OS</strong>
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osSph)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osCyl)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osAxis)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osAdd)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osBC)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osDia)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osDVA)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osNVA)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osND)}
            </td>
          </tr>

          {/* OU (Both Eyes) Row - if available */}
          {(data.ouDVA || data.ouNVA) && (
            <tr>
              <td className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 text-center">
                <strong>OU</strong>
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                {getDisplayValue(data.ouDVA)}
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                {getDisplayValue(data.ouNVA)}
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  if (!hasAnyContactLensData) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.4, ease: 'easeOut' }}
        className="group">
        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-lg border border-white/30 dark:border-white/15 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all duration-300">
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-white/20 dark:to-white/8 pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-slate-800 dark:text-slate-200 font-semibold text-lg tracking-wide">
                  Contact Lenses
                </h3>
                {isLoading && (
                  <div className="flex items-center gap-1 ml-2">
                    <Loader2 className="w-3 h-3 animate-spin text-eyevantage-primary" />
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      Loading...
                    </span>
                  </div>
                )}
              </div>
            </div>
            {isLoading ? (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-md">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'linear',
                  }}>
                  <Loader2 className="w-4 h-4" />
                </motion.div>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}>
                  Fetching section content...
                </motion.span>
              </motion.div>
            ) : (
              <div className="text-slate-500 dark:text-slate-400 text-sm italic">
                No contact lens data available yet
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.4, ease: 'easeOut' }}
        className="group space-y-4">
        {/* Main Contact Lenses Card with Integrated Subsections */}
        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-lg border border-white/30 dark:border-white/15 rounded-2xl p-2 shadow-md hover:shadow-lg transition-all duration-300">
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-white/20 dark:to-white/8 pointer-events-none" />
          <div className="relative z-10">
            {/* Main Header */}
            <div className="p-2 border-b border-white/20 dark:border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-slate-800 dark:text-slate-200 font-semibold text-xl tracking-wide">
                    Contact Lenses
                  </h3>
                  {isLoading && (
                    <div className="flex items-center gap-1 ml-2">
                      <Loader2 className="w-3 h-3 animate-spin text-eyevantage-primary" />
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        Loading...
                      </span>
                    </div>
                  )}
                </div>
                {hasAnyContactLensData && (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditAllClick();
                      }}
                      size="sm"
                      className="h-8 px-3 bg-slate-100/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-gray-400/40 dark:border-white/20 text-slate-700 dark:text-slate-300 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                      disabled={isLoading}>
                      <Edit3 className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAcceptAll();
                      }}
                      size="sm"
                      className="h-8 px-3 bg-emerald-100/50 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-emerald-600/60 dark:border-white/20 text-emerald-700 dark:text-emerald-700 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                      disabled={isLoading}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Accept
                    </Button>
                  </div>
                )}
              </div>

              {error && (
                <div className="text-red-600 dark:text-red-400 text-sm mt-2">
                  <span className="font-medium">Error:</span> {error}
                </div>
              )}
            </div>

            {/* Individual Contact Lens Subsections */}
            <div className="p-2 space-y-2">
              {contactLensSections.map((section, sectionIndex) => {
                const isAccepted = acceptedSections.has(section.key);

                return (
                  <div
                    key={section.key}
                    className={`${
                      sectionIndex > 0
                        ? 'border-t border-white/20 dark:border-white/10 pt-4'
                        : ''
                    }`}>
                    {/* Subsection Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <h4 className="text-slate-700 dark:text-slate-300 font-semibold text-lg">
                          {section.title}
                        </h4>
                        {isAccepted && (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        )}
                      </div>
                    </div>

                    {/* Contact Lens Table */}
                    {renderContactLensTable(section.data)}

                    {/* Additional Information */}
                    {(section.data.usage || section.data.notes) && (
                      <div className="mt-2 p-1 space-y-2">
                        {section.data.usage && (
                          <div>
                            <h5 className="text-md font-semibold text-slate-700 dark:text-slate-300">
                              Usage
                            </h5>
                            <p className="text-md text-slate-600 dark:text-slate-400 whitespace-pre-line">
                              {section.data.usage}
                            </p>
                          </div>
                        )}

                        {section.data.notes && (
                          <div>
                            <h5 className="text-md font-semibold text-slate-700 dark:text-slate-300">
                              Notes
                            </h5>
                            <p className="text-md text-slate-600 dark:text-slate-400">
                              {section.data.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Lens Details for finalsoft type (integrated table) */}
                    {section.key === 'finalsoft' &&
                      (section.data.odLensManufacturer ||
                        section.data.osLensManufacturer ||
                        section.data.odLensName ||
                        section.data.osLensName ||
                        section.data.odLensColor ||
                        section.data.osLensColor ||
                        section.data.odLensEnhOpq ||
                        section.data.osLensEnhOpq) && (
                        <div className="mt-3">
                          <h5 className="text-md font-semibold text-slate-700 dark:text-slate-300 mb-2">
                            Lens Details (Final Soft)
                          </h5>
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr>
                                  <th className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 text-center">
                                    Eye
                                  </th>
                                  <th className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 text-center">
                                    Manufacturer
                                  </th>
                                  <th className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 text-center">
                                    Lens Name
                                  </th>
                                  <th className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 text-center">
                                    Color
                                  </th>
                                  <th className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 text-center">
                                    Enhancement
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 text-center">
                                    <strong>OD</strong>
                                  </td>
                                  <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                                    {getDisplayValue(
                                      section.data.odLensManufacturer
                                    )}
                                  </td>
                                  <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                                    {getDisplayValue(section.data.odLensName)}
                                  </td>
                                  <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                                    {getDisplayValue(section.data.odLensColor)}
                                  </td>
                                  <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                                    {getDisplayValue(section.data.odLensEnhOpq)}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 text-center">
                                    <strong>OS</strong>
                                  </td>
                                  <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                                    {getDisplayValue(
                                      section.data.osLensManufacturer
                                    )}
                                  </td>
                                  <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                                    {getDisplayValue(section.data.osLensName)}
                                  </td>
                                  <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                                    {getDisplayValue(section.data.osLensColor)}
                                  </td>
                                  <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                                    {getDisplayValue(section.data.osLensEnhOpq)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent
          className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg border border-white/30 dark:border-white/15"
          onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="text-slate-800 dark:text-slate-200 font-semibold text-2xl">
              Edit Contact Lens
            </DialogTitle>
          </DialogHeader>

          <div
            className="space-y-4 overflow-y-auto"
            style={{ scrollbarWidth: 'none', height: '75vh' }}>
            {/* Render each contact lens type as a separate section */}
            {Object.entries(editValues).map(
              ([contactLensType, contactLensData], index) => {
                const title =
                  contactLensType.charAt(0).toUpperCase() +
                  contactLensType.slice(1) +
                  ' Contact Lens';

                return (
                  <div
                    key={contactLensType}
                    className="space-y-2 p-4 border bg-white dark:bg-slate-600/50 border-gray-400/40 dark:border-white/15 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
                    <h4 className="text-xl font-semibold text-slate-700 dark:text-blue-300 mb-4">
                      <span className="border-b-2 border-purple-500/80 dark:border-blue-300/80 h-1">
                        {title}
                      </span>
                    </h4>

                    {/* Table Header */}
                    <div className="grid grid-cols-10 bg-slate-300/50 dark:bg-blue-300/50 pl-2 pr-2 rounded-lg">
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300">
                        Eye
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        Sph
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        Cyl
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        Axis
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        Add
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        BC
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        Dia
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        DVA
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        NVA
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        N/D
                      </div>
                    </div>

                    {/* OD (Right Eye) Row */}
                    <div className="grid grid-cols-10 gap-2 items-center p-3 pb-0">
                      <div className="text-lg font-bold text-slate-700 dark:text-slate-300">
                        OD
                      </div>
                      <Input
                        value={contactLensData?.odSph || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'odSph',
                            e.target.value
                          )
                        }
                        placeholder="e.g., -2.50"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.odCyl || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'odCyl',
                            e.target.value
                          )
                        }
                        placeholder="e.g., -0.75"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.odAxis || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'odAxis',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 90"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.odAdd || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'odAdd',
                            e.target.value
                          )
                        }
                        placeholder="e.g., Mid"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.odBC || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'odBC',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 8.2"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.odDia || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'odDia',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 14.0"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.odDVA || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'odDVA',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 20/20"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.odNVA || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'odNVA',
                            e.target.value
                          )
                        }
                        placeholder="e.g., J1"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.odND || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'odND',
                            e.target.value
                          )
                        }
                        placeholder="e.g., N"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                    </div>

                    {/* OS (Left Eye) Row */}
                    <div className="grid grid-cols-10 gap-2 items-center p-3 pb-0">
                      <div className="text-lg font-bold text-slate-700 dark:text-slate-300">
                        OS
                      </div>
                      <Input
                        value={contactLensData?.osSph || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'osSph',
                            e.target.value
                          )
                        }
                        placeholder="e.g., -2.25"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.osCyl || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'osCyl',
                            e.target.value
                          )
                        }
                        placeholder="e.g., -0.50"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.osAxis || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'osAxis',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 85"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.osAdd || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'osAdd',
                            e.target.value
                          )
                        }
                        placeholder="e.g., Mid"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.osBC || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'osBC',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 8.0"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.osDia || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'osDia',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 14.0"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.osDVA || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'osDVA',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 20/25"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.osNVA || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'osNVA',
                            e.target.value
                          )
                        }
                        placeholder="e.g., J2"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.osND || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'osND',
                            e.target.value
                          )
                        }
                        placeholder="e.g., D"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                    </div>

                    {/* OU (Both Eyes) Row */}
                    <div className="grid grid-cols-10 gap-2 items-center p-3">
                      <div className="text-lg font-bold text-slate-700 dark:text-slate-300">
                        OU
                      </div>
                      <div className="col-span-6"></div>
                      <Input
                        value={contactLensData?.ouDVA || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'ouDVA',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 20/20"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={contactLensData?.ouNVA || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'ouNVA',
                            e.target.value
                          )
                        }
                        placeholder="e.g., J1"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <div></div>
                    </div>

                    {/* Usage Information */}
                    <div className="space-y-2">
                      <Label className="text-md text-slate-600 dark:text-slate-400">
                        Usage Information
                      </Label>
                      <Textarea
                        value={contactLensData?.usage || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'usage',
                            e.target.value
                          )
                        }
                        placeholder="Enter usage information (one per line):&#10;Weekend Wear&#10;Part-time&#10;Daily wear"
                        className="h-16 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Note: Each line will be treated as a separate usage type
                      </p>
                    </div>

                    {/* Additional Lens Details for finalsoft */}
                    {contactLensType === 'finalsoft' && (
                      <div className="space-y-4">
                        <h5 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
                          Lens Details (Final Soft)
                        </h5>

                        {/* Header */}
                        <div className="grid grid-cols-5 bg-slate-300/50 dark:bg-blue-300/50 pl-2 pr-2 rounded-lg">
                          <div className="text-lg font-semibold text-slate-700 dark:text-slate-300">
                            Eye
                          </div>
                          <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                            Manufacturer
                          </div>
                          <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                            Lens Name
                          </div>
                          <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                            Color
                          </div>
                          <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                            Enhancement
                          </div>
                        </div>

                        {/* OD (Right Eye) Row */}
                        <div className="grid grid-cols-5 gap-2 items-center p-3 pb-0">
                          <div className="text-lg font-bold text-slate-700 dark:text-slate-300">
                            OD
                          </div>
                          <Input
                            value={contactLensData?.odLensManufacturer || ''}
                            onChange={(e) =>
                              handleInputChange(
                                contactLensType,
                                'odLensManufacturer',
                                e.target.value
                              )
                            }
                            placeholder="Manufacturer (e.g., Vistakon)"
                            className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                          />
                          <Input
                            value={contactLensData?.odLensName || ''}
                            onChange={(e) =>
                              handleInputChange(
                                contactLensType,
                                'odLensName',
                                e.target.value
                              )
                            }
                            placeholder="Lens Name (e.g., Acuvue 2)"
                            className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                          />
                          <Input
                            value={contactLensData?.odLensColor || ''}
                            onChange={(e) =>
                              handleInputChange(
                                contactLensType,
                                'odLensColor',
                                e.target.value
                              )
                            }
                            placeholder="Color (e.g., Blue)"
                            className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                          />
                          <Input
                            value={contactLensData?.odLensEnhOpq || ''}
                            onChange={(e) =>
                              handleInputChange(
                                contactLensType,
                                'odLensEnhOpq',
                                e.target.value
                              )
                            }
                            placeholder="Enhancement (e.g., Opq)"
                            className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                          />
                        </div>

                        {/* OS (Left Eye) Row */}
                        <div className="grid grid-cols-5 gap-2 items-center p-3">
                          <div className="text-lg font-bold text-slate-700 dark:text-slate-300">
                            OS
                          </div>
                          <Input
                            value={contactLensData?.osLensManufacturer || ''}
                            onChange={(e) =>
                              handleInputChange(
                                contactLensType,
                                'osLensManufacturer',
                                e.target.value
                              )
                            }
                            placeholder="Manufacturer (e.g., Vistakon)"
                            className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                          />
                          <Input
                            value={contactLensData?.osLensName || ''}
                            onChange={(e) =>
                              handleInputChange(
                                contactLensType,
                                'osLensName',
                                e.target.value
                              )
                            }
                            placeholder="Lens Name (e.g., Acuvue 2)"
                            className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                          />
                          <Input
                            value={contactLensData?.osLensColor || ''}
                            onChange={(e) =>
                              handleInputChange(
                                contactLensType,
                                'osLensColor',
                                e.target.value
                              )
                            }
                            placeholder="Color (e.g., Blue)"
                            className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                          />
                          <Input
                            value={contactLensData?.osLensEnhOpq || ''}
                            onChange={(e) =>
                              handleInputChange(
                                contactLensType,
                                'osLensEnhOpq',
                                e.target.value
                              )
                            }
                            placeholder="Enhancement (e.g., Enh)"
                            className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                          />
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div className="space-y-2">
                      <Label className="text-md text-slate-600 dark:text-slate-400">
                        Notes
                      </Label>
                      <Textarea
                        value={contactLensData?.notes || ''}
                        onChange={(e) =>
                          handleInputChange(
                            contactLensType,
                            'notes',
                            e.target.value
                          )
                        }
                        placeholder="Enter any additional notes or observations..."
                        className="h-16 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                      />
                    </div>
                  </div>
                );
              }
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-white/80 dark:bg-slate-800/80 border border-gray-400/40 dark:border-white/20">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleSaveEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-500 hover:to-blue-800 text-white">
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContactLensesSection;
